/*
 * Copyright (c) 2008-present Sonatype, Inc.
 *
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/pro/attributions
 * Sonatype and Sonatype Nexus are trademarks of Sonatype, Inc. Apache Maven is a trademark of the Apache Foundation.
 * M2Eclipse is a trademark of the Eclipse Foundation. All other trademarks are the property of their respective owners.
 */
import React from 'react';
import {useMachine} from '@xstate/react';
import {faServer} from '@fortawesome/free-solid-svg-icons';

import {
  ContentBody,
  Page,
  PageHeader,
  PageTitle,
  Section,
  Utils,
  FormUtils,
} from '@sonatype/nexus-ui-plugin';

import {
  NxButton,
  NxTooltip,
  NxForm,
  NxTextInput,
  NxFormGroup,
} from '@sonatype/react-shared-components';

import DataStoreConfigurationMachine from './DataStoreConfigurationMachine';
import UIStrings from '../../../../constants/UIStrings';

const FIELDS = UIStrings.DATASTORE_CONFIGURATION.FIELDS;

export default function DataStoreConfiguration() {
  const [current, send] = useMachine(DataStoreConfigurationMachine, {devTools: true});

  const {isPristine, validationErrors, saveError, loadError} = current.context;
  const isLoading = current.matches('loading');
  const isSaving = current.matches('saving');
  const isInvalid = Utils.isInvalid(validationErrors);

  function save() {
    send('SAVE');
  }

  function discard() {
    send('RESET');
  }

  function retry() {
    send('RETRY');
  }

  return <Page>
    <PageHeader><PageTitle icon={faServer} {...UIStrings.DATASTORE_CONFIGURATION.MENU}/></PageHeader>
    <ContentBody className='nxrm-datastore-configuration'>
      <Section className="user-account-settings">
        <NxForm
            loading={isLoading}
            loadError={loadError}
            doLoad={retry}
            onSubmit={save}
            submitError={saveError}
            submitMaskState={isSaving ? false : null}
            submitBtnText={UIStrings.SETTINGS.SAVE_BUTTON_LABEL}
            submitMaskMessage={UIStrings.SAVING}
            validationErrors={FormUtils.saveTooltip({isPristine, isInvalid})}
            additionalFooterBtns={
              <NxTooltip title={FormUtils.discardTooltip({isPristine})}>
                <NxButton type="button" className={isPristine && 'disabled'} onClick={discard}>
                  {UIStrings.SETTINGS.DISCARD_BUTTON_LABEL}
                </NxButton>
              </NxTooltip>
            }
        >
          <NxFormGroup label={FIELDS.jdbcUrlLabel} isRequired>
            <NxTextInput
                {...FormUtils.fieldProps('jdbcUrl', current)}
                className="nx-text-input--long"
                disabled
            />
          </NxFormGroup>
          <NxFormGroup label={FIELDS.usernameLabel} isRequired>
            <NxTextInput
                {...FormUtils.fieldProps('username', current)}
                disabled
            />
          </NxFormGroup>
          <NxFormGroup label={FIELDS.schemaLabel} isRequired>
            <NxTextInput
                {...FormUtils.fieldProps('schema', current)}
                disabled
            />
          </NxFormGroup>
          <NxFormGroup label={FIELDS.maxConnectionPoolLabel}>
            <NxTextInput
                {...FormUtils.fieldProps('maximumConnectionPool', current)}
                onChange={FormUtils.handleUpdate('maximumConnectionPool', send)}
            />
          </NxFormGroup>
          <NxFormGroup label={FIELDS.advancedLabel}>
            <NxTextInput
                {...FormUtils.fieldProps('advanced', current)}
                onChange={FormUtils.handleUpdate('advanced', send)}
                className="nx-text-input--long"
                type="textarea"
            />
          </NxFormGroup>
        </NxForm>
      </Section>
    </ContentBody>
  </Page>;
}
